import type {
  IntegrationTool,
  IntegrationToolMethod,
  ToolState,
} from '@/features/Integrations/types/integration';

/** Ordem do seletor: do mais livre ao mais restrito. */
export const TOOL_STATES: readonly ToolState[] = ['enabled', 'confirm', 'blocked'];

export type ToolStates = Record<string, ToolState>;

function isToolState(value: unknown): value is ToolState {
  return typeof value === 'string' && (TOOL_STATES as readonly string[]).includes(value);
}

/** Estado de uma tool; ausente (ou valor desconhecido) = `enabled`, como no runtime. */
export function toolState(states: ToolStates | null | undefined, name: string): ToolState {
  const value = states?.[name];
  return isToolState(value) ? value : 'enabled';
}

/** Novo mapa com o estado trocado — o original fica intacto para comparar depois. */
export function setToolState(
  states: ToolStates | null | undefined,
  name: string,
  state: ToolState,
): ToolStates {
  return { ...(states ?? {}), [name]: state };
}

/**
 * O mapa que vai para o `PUT .../tools`: um estado por tool que existe hoje.
 *
 * Vai explícito, `enabled` inclusive — se a api mesclar em vez de substituir, omitir a
 * chave deixaria a tool presa no estado antigo. E some o estado de tool que não existe
 * mais, para o mapa não acumular nomes mortos.
 */
export function normalizeToolStates(
  states: ToolStates | null | undefined,
  tools: readonly Pick<IntegrationTool, 'name'>[],
): ToolStates {
  const out: ToolStates = {};
  for (const tool of tools) out[tool.name] = toolState(states, tool.name);
  return out;
}

/** Algum estado efetivo mudou entre o gravado e o rascunho? */
export function toolStatesChanged(
  saved: ToolStates | null | undefined,
  draft: ToolStates | null | undefined,
  tools: readonly Pick<IntegrationTool, 'name'>[],
): boolean {
  return tools.some((tool) => toolState(saved, tool.name) !== toolState(draft, tool.name));
}

/** Quantas tools em cada estado — o resumo acima da tabela. */
export function countToolStates(
  states: ToolStates | null | undefined,
  tools: readonly Pick<IntegrationTool, 'name'>[],
): Record<ToolState, number> {
  const counts: Record<ToolState, number> = { enabled: 0, confirm: 0, blocked: 0 };
  for (const tool of tools) counts[toolState(states, tool.name)] += 1;
  return counts;
}

/**
 * Método exibido: o da definição REST, ou — para tool de servidor MCP, que não tem método —
 * o mesmo que a api sintetiza para o gateway: `readOnlyHint` → `GET`, senão `POST` (AB#87477).
 */
export function displayMethod(tool: IntegrationTool): IntegrationToolMethod {
  if (tool.method) return tool.method;
  return tool.annotations?.readOnlyHint ? 'GET' : 'POST';
}
